import React from 'react';
import { MapPin, Thermometer, Calendar } from 'lucide-react';

/**
 * Компонент с дополнительными параметрами расчета
 */
const AdditionalParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-blue-500" />
        Расположение и климатические условия
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Расположение бассейна */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Расположение бассейна
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="poolLocation"
                value="outdoor"
                checked={formData.poolLocation === 'outdoor'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Открытый
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="poolLocation"
                value="indoor"
                checked={formData.poolLocation === 'indoor'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Крытый
              </span>
            </label>
          </div>
        </div>
        
        {/* Климатические условия */}
        <div>
          <label htmlFor="climate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Thermometer className="w-4 h-4 inline-block mr-1" />
            Климатические условия
          </label>
          <select
            id="climate"
            name="climate"
            value={formData.climate}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="cold">Холодный климат (север России)</option>
            <option value="moderate">Умеренный климат (средняя полоса)</option>
            <option value="hot">Жаркий климат (юг России)</option>
          </select>
        </div>
        
        {/* Длительность сезона (для открытых бассейнов) */}
        {formData.poolLocation === 'outdoor' && (
          <div>
            <label htmlFor="seasonLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline-block mr-1" />
              Длительность сезона
            </label>
            <select
              id="seasonLength"
              name="seasonLength"
              value={formData.seasonLength}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              <option value="2">2 месяца (июнь-июль)</option>
              <option value="3">3 месяца (июнь-август)</option>
              <option value="4">4 месяца (май-сентябрь)</option>
              <option value="5">5 месяцев (май-октябрь)</option>
              <option value="6">6 месяцев (апрель-октябрь)</option>
            </select>
          </div>
        )}
        
        {/* Тип укрытия */}
        <div>
          <label htmlFor="coverType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип укрытия бассейна
          </label>
          <select
            id="coverType"
            name="coverType"
            value={formData.coverType}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="none">Без укрытия</option>
            <option value="standard">Стандартное покрытие</option>
            <option value="solar">Солнечное покрытие (Solar Cover)</option>
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Влияет на испарение и теплопотери
          </p>
        </div>
        
        {/* Система подогрева */}
        <div>
          <label htmlFor="heatingSystem" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Система подогрева
          </label>
          <select
            id="heatingSystem"
            name="heatingSystem"
            value={formData.heatingSystem}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="none">Без подогрева</option>
            <option value="electric">Электрический нагреватель</option>
            <option value="heat_pump">Тепловой насос</option>
            <option value="solar">Солнечный нагреватель</option>
          </select>
        </div>
      </div>
      
      {/* Инфо-блок для открытых бассейнов */}
      {formData.poolLocation === 'outdoor' && (
        <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Важно:</strong> Для открытых бассейнов в России характерны сезонное использование и повышенное испарение воды. 
            В зависимости от региона сезон эксплуатации может составлять от 2 до 6 месяцев в году.
          </p>
        </div>
      )}
      
      {/* Инфо-блок для крытых бассейнов */}
      {formData.poolLocation === 'indoor' && (
        <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
          <p className="text-sm text-indigo-700 dark:text-indigo-300">
            <strong>Важно:</strong> Для крытых бассейнов необходимо учитывать затраты на вентиляцию и осушение воздуха. 
            Рекомендуется установка системы вентиляции производительностью не менее 8 м³/ч на 1 м² водной поверхности.
          </p>
        </div>
      )}
      
      {/* Рекомендации по системе подогрева */}
      {formData.heatingSystem !== 'none' && (
        <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            <strong>Рекомендация:</strong> {
              formData.heatingSystem === 'electric' ? 
                'Для бассейнов с электрическим подогревом потребуется примерно 1.5 кВт мощности на каждый кубический метр воды для поддержания комфортной температуры.' :
              formData.heatingSystem === 'heat_pump' ?
                'Тепловой насос - наиболее энергоэффективное решение для подогрева воды в бассейне. Рекомендованная мощность: 1 кВт на каждые 4 м³ воды.' :
              'Солнечные нагреватели эффективны в южных регионах России. Рекомендуемая площадь солнечных коллекторов: 50% от площади зеркала воды.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default AdditionalParametersSection;